import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '../services/notification.service';

// Sentinel emitted by the prompt-generation skill (apps/maporium/src/app/ai/
// skills/prompt-generation.skill.ts) when every retry attempt fails to parse
// the AI response into a valid smap. Matching the prefix decouples the UI
// wording from the backend.
const SMAP_PARSE_FAILED_PREFIX = 'SMAP_PARSE_FAILED';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;'
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);
}

// Render the smap-generation failure notification. When the backend returns
// the SMAP_PARSE_FAILED sentinel we surface the dedicated parse-failed copy
// and echo the user's original prompt; for any other backend error we keep
// the generic "Generation Failed" notification so we don't suppress useful
// detail (rate limits, billing issues, etc.).
//
// This is a plain function, so it cannot inject TranslateService; the caller
// (which already has one) passes it in. This keeps the change minimal while
// letting the copy be translated.
export function showSmapGenerationFailure(
  notificationService: NotificationService,
  translate: TranslateService,
  backendError: string | undefined | null,
  prompt: string,
  // MAP-1579: job id of the generation, so this completion notification
  // supersedes the "generation in progress" one still on screen.
  taskKey?: string
): void {
  const error = (backendError || '').trim();
  if (error.startsWith(SMAP_PARSE_FAILED_PREFIX)) {
    const echoed = escapeHtml(prompt);
    notificationService.error(
      `${translate.instant('SMAP_GEN.PARSE_FAILED_BODY')}<br><br>Your prompt was:` +
      `<div class="notification-prompt-echo">"${echoed}"</div>`,
      {title: translate.instant('SMAP_GEN.PARSE_FAILED_TITLE'), duration: 0, taskKey}
    );
    return;
  }

  const message = error || translate.instant('SMAP_GEN.GENERATION_FAILED_DEFAULT');
  notificationService.error(message, {title: translate.instant('CREATE_MAP_DIALOG.GENERATION_FAILED_TITLE'), duration: 0, taskKey});
}
